import {
  useGameStatus,
  useTriviaStore,
} from '@wwe-portal/web/providers/stores/trivia-store';
import { ProgressBar } from '@wwe-portal/ui/components/progress';
import { useTranslations } from 'next-intl';

import { FC, SyntheticEvent, useMemo } from 'react';
import { TriviaRumbleQuestion } from '@wwe-portal/entities/trivia-rumble.type';
import { cn } from '@wwe-portal/ui/lib/lib';

import { NextQuestionTransition } from './next-question-transition';
import CountUp from 'react-countup';

import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';

import styles from './trivia-game-player.module.css';
import { TriviaGameReadyAnimation } from './ready-animation';
import { useSubmitAnswerMutation } from '@wwe-portal/web/providers/queries/trivia-rumble.mutation';
import { TriviaGameOptions } from '@wwe-portal/ui/features/trivia/game-options';

const TriviaGamePlay: FC<{
  gameData: {
    questionIndex: number;
    currentQuestion: TriviaRumbleQuestion;
    currentAnswer: string | undefined;
    correctAnswer: string | undefined;
    previousPoint: number;
    currentPoint: number;
  };
  config: {
    numberOfQuestions: number;
  };
  isCheckingAnswer: boolean;
  onAnswer: ({
    event,
    optionId,
  }: {
    event: SyntheticEvent;
    optionId: string;
  }) => void;
}> = ({ gameData, config, onAnswer, isCheckingAnswer }) => {
  const t = useTranslations();
  const gameQuestion = gameData.currentQuestion;
  const gameStatus = useGameStatus();
  const isWaitingNextQuestion = gameStatus === 'waiting';
  const isCorrectAnswer =
    gameData.correctAnswer != null &&
    gameData.correctAnswer === gameData.currentAnswer;

  return (
    <div className="flex-1">
      <div className="mb-[30px] flex justify-between">
        <div className="bg-back300 text-md text-text600 flex items-center rounded-[6px] px-5 pb-[5px] pt-[5px] md:px-[10px]">
          {t('question')}
          <span className="text-text300 mx-1 mb-0 text-xl">
            {gameData.questionIndex + 1}
          </span>
          / {config?.numberOfQuestions || 0}
        </div>
        <div className="bgColor-bgColor15 text-text600 flex items-center gap-1 rounded-[6px] px-5 py-[5px]">
          <IcoMoonIcon
            iconName="medal-fill"
            className={cn('mr-[5px] h-6 w-6', {
              'point-icon-pulsing text-green700':
                isWaitingNextQuestion || isCorrectAnswer,
            })}
          />
          <span className="text-text400">
            <CountUp
              start={gameData.previousPoint}
              end={gameData.currentPoint}
            />
          </span>
          {t('point')}
        </div>
      </div>
      {isWaitingNextQuestion ? (
        <div className={styles.questionStep}>
          <NextQuestionTransition
            questionIndex={gameData.questionIndex}
            numberOfQuestions={config.numberOfQuestions}
          />
        </div>
      ) : (
        <div>
          <div
            className={cn(
              styles.questionBlock,
              'bg-back300 text-text400 rounded-[10px] p-5 md:p-[30px]'
            )}
          >
            {gameQuestion?.image && (
              <div className="bg-text800 mb-[10px] inline-block rounded-[4px] p-[5px]">
                <img src={gameQuestion?.image} alt="trivia_question_art" />
              </div>
            )}
            <p>{gameQuestion.question || ''}</p>
          </div>
          <TriviaGameOptions
            options={gameData.currentQuestion.options}
            userAnswer={gameData.currentAnswer}
            correctAnswer={gameData.correctAnswer}
            onAnswer={onAnswer}
            isCheckingAnswer={isCheckingAnswer}
          />
        </div>
      )}
    </div>
  );
};

const GameStartCountdown = () => {
  const t = useTranslations();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-16">
      <TriviaGameReadyAnimation />
      <p className={styles.getReadyText}>{t('get_ready')}</p>
    </div>
  );
};

const GameHeaderInfo = ({ duration }: { duration: number }) => {
  const questionCountdown = useTriviaStore((state) => state.questionCountdown);
  const secondsLeft = useMemo(() => {
    if (questionCountdown?.msLeft == null) return duration;
    return Math.ceil((questionCountdown?.msLeft || 0) / 1000);
  }, [questionCountdown, duration]);

  const value = useMemo(() => {
    if (questionCountdown?.msLeft == null) return duration * 1000;
    if (questionCountdown?.stopped) return questionCountdown.msLeft;
    if (secondsLeft >= duration) return duration * 1000;
    return 0;
  }, [questionCountdown, secondsLeft]);

  return (
    <div className="relative mb-[30px] mt-[10px]">
      <div
        className={cn(
          styles.answerTimeCounter,
          'absolute flex items-center justify-center font-semibold'
        )}
      >
        {questionCountdown?.msLeft == null ? duration : Math.ceil(secondsLeft)}
      </div>
      <ProgressBar
        value={questionCountdown?.msLeft ?? value}
        maxValue={duration * 1000}
        hideValue
        color="#00baff"
        animationDuration={0}
      />
    </div>
  );
};

export const TriviaGamePlayer = () => {
  const gameData = useTriviaStore((state) => state.gameData);
  const config = useTriviaStore((state) => state.gameConfig);

  const { mutate: choseAnswerMutate, isPending: isCheckingAnswer } =
    useSubmitAnswerMutation();
  const gameStatus = useGameStatus();

  if (!gameData || !config) return;

  return (
    <>
      <div className="flex h-full flex-col justify-between overflow-hidden">
        {config.questionTime && (
          <GameHeaderInfo duration={config.questionTime} />
        )}
        {gameStatus !== 'ready' && (
          <TriviaGamePlay
            gameData={gameData}
            config={config}
            isCheckingAnswer={isCheckingAnswer}
            onAnswer={choseAnswerMutate}
          />
        )}
        {gameStatus === 'ready' && <GameStartCountdown />}
      </div>
    </>
  );
};
